import { KnownProduct } from "./KnownProduct.tsx";

import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  CheckBox,
  MultipleSelect,
  MultipleSelectOption,
  PrimaryButton,
  Select,
  TextInput,
} from "@normasteaching/norma-ui";
import { PropsWithChildren, useState, useEffect } from "react";
import { HeyLightProductDTO } from "../../HeyLight/useHeyLightProductById.ts";
export type CreateHeyLightProductFormValues = {
  productName: string;
  internalProductName: string;
  priceInEuros: number;
  productClass: KnownProduct;
  purchaseAcTags?: string[];
  refundAcTags?: string[];
  successUrl: string;
  failureUrl: string;
  bundledProductIds?: string[];
  allowedTerms: number[];
  peopleCount: number;

  fpsLessonsDurationInWeeks?: number;
  fpsUnlimitedLessonsPerWeek?: boolean;
  fpsLessonsPerWeek?: number;
  fpsTotalLessons?: number;
  fpsExtraWeek?: boolean;
  fpsWarrantyEnabled?: boolean;
  fpsWarrantyMinAttendedLessons?: number;
  analyticsTag?: string;

  userGroupSlug?: string;
  doNotEnroll: boolean;
  doNotMerge: boolean;
};

const validationSchema = yup.object({
  productName: yup.string().required(),
  internalProductName: yup.string().required(),
  priceInEuros: yup.number().required(),
  productClass: yup
    .string()
    .oneOf(Object.values(KnownProduct), "Invalid product class")
    .required(),
  purchaseAcTags: yup.array().of(yup.string().required()),
  refundAcTags: yup.array().of(yup.string().required()),
  successUrl: yup.string().required(),
  failureUrl: yup.string().required(),
  bundledProductIds: yup.array().of(yup.string().required()),
  allowedTerms: yup.array().of(yup.number().required()).required(),
  peopleCount: yup
    .number()
    .min(1, "peopleCount must be at least 1")
    .default(1)
    .required(),

  fpsLessonsDurationInWeeks: yup.number().when("productClass", {
    is: KnownProduct.FPS,
    then: (schema) => schema.required("Durata obbligatoria"),
    otherwise: (schema) => schema.optional(),
  }),

  fpsUnlimitedLessonsPerWeek: yup.boolean(),

  fpsTotalLessons: yup
    .number()
    .when(["productClass", "fpsUnlimitedLessonsPerWeek"], {
      is: (productClass: KnownProduct, unlimited: boolean) =>
        productClass === KnownProduct.FPS && unlimited,
      then: (schema) =>
        schema.min(1).required("Numero totale lezioni obbligatorio"),
      otherwise: (schema) => schema.optional(),
    }),

  fpsLessonsPerWeek: yup
    .number()
    .when(["productClass", "fpsUnlimitedLessonsPerWeek"], {
      is: (productClass: KnownProduct, unlimited: boolean) =>
        productClass === KnownProduct.FPS && !unlimited,
      then: (schema) =>
        schema.min(1).required("Numero di lezioni obbligatorio"),
      otherwise: (schema) => schema.optional(),
    }),

  fpsWarrantyEnabled: yup.boolean(),

  fpsWarrantyMinAttendedLessons: yup
    .number()
    .when(["productClass", "fpsWarrantyEnabled"], {
      is: (productClass: KnownProduct, fpsWarrantyEnabled: boolean) =>
        productClass === KnownProduct.FPS && fpsWarrantyEnabled,
      then: (schema) =>
        schema
          .min(1)
          .required("Numero minimo lezioni obbligatorio per la garanzia"),
      otherwise: (schema) => schema.optional(),
    }),
  analyticsTag: yup.string().optional(),
  userGroupSlug: yup.string().optional(),
  doNotEnroll: yup.boolean().default(false),
  doNotMerge: yup.boolean().default(false),
});

function InputWithLabel({
  children,
  label,
}: PropsWithChildren<{ label: string }>) {
  return (
    <div className={"flex flex-col gap-1 flex-1"}>
      <label className={"font-medium"}>{label}</label>
      {children}
    </div>
  );
}
export function CreateHeyLightProductForm({
  initialKnownPurchaseAcTags,
  initialKnownRefundAcTags,
  products,
  creating,
  onSubmit,
}: {
  initialKnownPurchaseAcTags: string[];
  initialKnownRefundAcTags: string[];
  products: HeyLightProductDTO[];
  onSubmit: (data: CreateHeyLightProductFormValues) => void;
  creating: boolean;
}) {
  function mapStringsToMultipleSelectOptions(
    strings: string[],
  ): MultipleSelectOption<string>[] {
    return strings.map((s) => ({
      id: s,
      label: s,
    }));
  }
  const [acPurchaseTagOptions, setAcPurchaseTagOptions] = useState<
    MultipleSelectOption<string>[]
  >(mapStringsToMultipleSelectOptions(initialKnownPurchaseAcTags));
  const [acRefundTagOptions, setAcRefundTagOptions] = useState<
    MultipleSelectOption<string>[]
  >(mapStringsToMultipleSelectOptions(initialKnownRefundAcTags));
  const {
    control,
    register,
    setValue,
    formState: { errors },
    handleSubmit,
    watch,
  } = useForm<CreateHeyLightProductFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      fpsUnlimitedLessonsPerWeek: true,
      fpsWarrantyEnabled: true,
      fpsExtraWeek: false,
      doNotEnroll: false,
      doNotMerge: false,
    },
  });

  const hasUnlimitedLessonsPerWeek = watch("fpsUnlimitedLessonsPerWeek");
  const hasWarrantyEnabled = watch("fpsWarrantyEnabled");

  useEffect(() => {
    if (hasUnlimitedLessonsPerWeek) {
      setValue("fpsTotalLessons", 10, { shouldValidate: true });
    } else {
      setValue("fpsLessonsPerWeek", 2, { shouldValidate: true });
    }
  }, [hasUnlimitedLessonsPerWeek, setValue]);

  useEffect(() => {
    if (!hasWarrantyEnabled) {
      setValue("fpsWarrantyMinAttendedLessons", 1, { shouldValidate: true });
    }
  }, [hasWarrantyEnabled, setValue]);

  const onSubmitInternal = async (data: CreateHeyLightProductFormValues) => {
    onSubmit(data);
  };

  const selectedClass = watch("productClass");

  return (
    <form className={"flex flex-col gap-4"}>
      <div className={"flex flex-col md:flex-row gap-4"}>
        <InputWithLabel label={"Nome prodotto"}>
          <TextInput
            {...register("productName")}
            errorMessage={errors?.productName?.message}
          />
        </InputWithLabel>
        <InputWithLabel label={"Nome prodotto interno"}>
          <TextInput
            {...register("internalProductName")}
            errorMessage={errors?.internalProductName?.message}
          />
        </InputWithLabel>
      </div>
      <div className={"flex flex-col md:flex-row gap-4"}>
        <div className="flex flex-1 gap-4">
          <InputWithLabel label={"Prezzo in Euro"}>
            <TextInput
              errorMessage={errors?.priceInEuros?.message}
              onWheel={(e) => {
                const el = e.target as HTMLInputElement;
                el?.blur();
                e.stopPropagation();
                setTimeout(() => {
                  el?.focus();
                }, 0);
              }}
              type={"number"}
              {...register("priceInEuros")}
            />
          </InputWithLabel>
          <InputWithLabel label="Numero di persone">
            <TextInput
              type="number"
              min={1}
              defaultValue={1}
              {...register("peopleCount")}
              errorMessage={errors?.peopleCount?.message}
            />
          </InputWithLabel>
        </div>

        <InputWithLabel label={"Classe prodotto"}>
          <Controller
            render={({ field }) => {
              return (
                <Select
                  errorMessage={errors?.productClass?.message}
                  value={field.value}
                  onChange={field.onChange}
                  options={Object.entries(KnownProduct).map(([key, value]) => {
                    return {
                      name: key,
                      id: value,
                    };
                  })}
                />
              );
            }}
            name={"productClass"}
            control={control}
          />
        </InputWithLabel>
      </div>
      <div className={"flex flex-col md:flex-row gap-4"}>
        <InputWithLabel label={"Active Campaign tag post acquisto"}>
          <Controller
            render={({ field }) => {
              return (
                <MultipleSelect
                  errorMessage={errors?.purchaseAcTags?.message}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  options={acPurchaseTagOptions}
                  allowCreateInfo={{
                    setOptions: setAcPurchaseTagOptions,
                  }}
                />
              );
            }}
            name={"purchaseAcTags"}
            control={control}
          />
        </InputWithLabel>
        <InputWithLabel label={"Active Campaign tag post refund"}>
          <Controller
            render={({ field }) => {
              return (
                <MultipleSelect
                  errorMessage={errors?.refundAcTags?.message}
                  value={field.value ?? []}
                  onChange={field.onChange}
                  options={acRefundTagOptions}
                  allowCreateInfo={{
                    setOptions: setAcRefundTagOptions,
                  }}
                />
              );
            }}
            name={"refundAcTags"}
            control={control}
          />
        </InputWithLabel>
      </div>
      <div className={"flex flex-col md:flex-row gap-4"}>
        <InputWithLabel label={"URL post successo"}>
          <TextInput
            {...register("successUrl")}
            errorMessage={errors?.successUrl?.message}
          />
        </InputWithLabel>
        <InputWithLabel label={"URL post fallimento"}>
          <TextInput
            {...register("failureUrl")}
            errorMessage={errors?.failureUrl?.message}
          />
        </InputWithLabel>
      </div>
      <div className={"flex flex-col md:flex-row gap-4"}>
        <InputWithLabel label={"Prodotti inclusi"}>
          <Controller
            render={({ field }) => {
              return (
                <MultipleSelect
                  errorMessage={errors?.bundledProductIds?.message}
                  searchable
                  value={field.value ?? []}
                  onChange={field.onChange}
                  options={products.map((p) => ({
                    id: p.id,
                    label: p.internalProductName,
                  }))}
                />
              );
            }}
            name={"bundledProductIds"}
            control={control}
          />
        </InputWithLabel>
        <InputWithLabel label={"Rate disponibili"}>
          <Controller
            render={({ field }) => {
              return (
                <MultipleSelect
                  errorMessage={errors?.allowedTerms?.message}
                  searchable
                  value={field.value?.map((v) => v.toString()) ?? []}
                  onChange={(v) => {
                    return field.onChange(v.map((s) => parseInt(s, 10)));
                  }}
                  options={Array.from(
                    {
                      length: 22,
                    },
                    (_, i) => ({
                      id: (i + 3).toString(),
                      label: `${i + 3} rate`,
                    }),
                  )}
                />
              );
            }}
            name={"allowedTerms"}
            control={control}
          />
        </InputWithLabel>
      </div>

      {selectedClass === KnownProduct.FPS && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col md:flex-row gap-4">
            <InputWithLabel label="Durata lezioni (settimane)">
              <TextInput
                type="number"
                {...register("fpsLessonsDurationInWeeks")}
                errorMessage={errors?.fpsLessonsDurationInWeeks?.message}
                min={1}
              />
            </InputWithLabel>
            <div className="flex flex-1 gap-4">
              <InputWithLabel label="RLC illimitate a settimana">
                <Controller
                  name="fpsUnlimitedLessonsPerWeek"
                  control={control}
                  render={({ field }) => (
                    <Select
                      errorMessage={errors?.fpsUnlimitedLessonsPerWeek?.message}
                      value={field.value ? "true" : "false"}
                      onChange={(val) => {
                        const newVal = val === "true";
                        field.onChange(newVal);

                        setValue("fpsExtraWeek", !newVal, {
                          shouldValidate: true,
                        });
                      }}
                      options={[
                        { id: "true", name: "Sì" },
                        { id: "false", name: "No" },
                      ]}
                    />
                  )}
                />
              </InputWithLabel>
              {watch("fpsUnlimitedLessonsPerWeek") ? (
                <InputWithLabel label="Numero totale lezioni">
                  <TextInput
                    type="number"
                    {...register("fpsTotalLessons")}
                    errorMessage={errors?.fpsTotalLessons?.message}
                    min={1}
                  />
                </InputWithLabel>
              ) : (
                <InputWithLabel label="Lezioni a settimana">
                  <TextInput
                    type="number"
                    {...register("fpsLessonsPerWeek")}
                    errorMessage={errors?.fpsLessonsPerWeek?.message}
                    min={1}
                  />
                </InputWithLabel>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <div className="flex flex-1 gap-4">
              <InputWithLabel label="Garanzia abilitata">
                <Controller
                  name="fpsWarrantyEnabled"
                  control={control}
                  render={({ field }) => (
                    <Select
                      errorMessage={errors?.fpsWarrantyEnabled?.message}
                      value={field.value ? "true" : "false"}
                      onChange={(val) => field.onChange(val === "true")}
                      options={[
                        { id: "true", name: "Sì" },
                        { id: "false", name: "No" },
                      ]}
                    />
                  )}
                />
              </InputWithLabel>

              {watch("fpsWarrantyEnabled") && (
                <InputWithLabel label="Minimo lezioni frequentate per garanzia">
                  <TextInput
                    type="number"
                    {...register("fpsWarrantyMinAttendedLessons")}
                    errorMessage={
                      errors?.fpsWarrantyMinAttendedLessons?.message
                    }
                    min={1}
                  />
                </InputWithLabel>
              )}
            </div>
          </div>
          <div className={"flex flex-col md:flex-row gap-4"}>
            <InputWithLabel label={"User group slug (opzionale)"}>
              <TextInput
                {...register("userGroupSlug")}
                errorMessage={errors?.userGroupSlug?.message}
              />
            </InputWithLabel>
            <InputWithLabel
              label={"Non iscrivere automaticamente (doNotEnroll)"}
            >
              <Controller
                name="doNotEnroll"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value ? "true" : "false"}
                    onChange={(val) => field.onChange(val === "true")}
                    options={[
                      { id: "false", name: "No" },
                      { id: "true", name: "Yes" },
                    ]}
                  />
                )}
              />
            </InputWithLabel>
          </div>
          <Controller
            name="doNotMerge"
            control={control}
            render={({ field }) => (
              <CheckBox
                label={<span>Non fare il merge (doNotMerge)</span>}
                value={field.value ?? false}
                onChange={field.onChange}
              />
            )}
          />
        </div>
      )}

      <div>
        <InputWithLabel label={"Analytics tag"}>
          <TextInput
            {...register("analyticsTag")}
            errorMessage={errors?.analyticsTag?.message}
          />
        </InputWithLabel>
      </div>

      <PrimaryButton
        loading={creating}
        label={"Crea"}
        onClick={handleSubmit(onSubmitInternal)}
      />
    </form>
  );
}
